import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, History, MessageCircle, Calendar, ChevronRight,
  Loader2, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_NAMES = {
  routeur: "ODYS'GUIDE",
  analyse: "ODYS'ANALYSE",
  ia: "ODYS'ASSISTANT",
  strategie: "ODYS'STRATEGIE",
  nego: "ODYS'NEGO",
  banque: "ODYS'BANQUE",
  travaux: "ODYS'TRAVAUX",
  fiscalite: "ODYS'FISCALITE",
  locataire: "ODYS'LOCATAIRE",
  marche: "ODYS'MARCHE",
  juridique: "ODYS'JURIDIQUE",
  notaire: "ODYS'NOTAIRE"
};

const AGENT_COLORS = {
  routeur: "#16233B",
  analyse: "#FF8C42",
  ia: "#10B981",
  strategie: "#8B5CF6",
  nego: "#EF4444",
  banque: "#3B82F6",
  travaux: "#F59E0B",
  fiscalite: "#EC4899",
  locataire: "#06B6D4",
  marche: "#14B8A6",
  juridique: "#8B5CF6",
  notaire: "#374151"
};

export default function ConversationsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/api/conversations/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/conversations/${conversationToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationToDelete));
      toast.success("Conversation supprimee");
    } catch (err) {
      console.error("Error deleting conversation:", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  const handleClearAll = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/conversations/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations([]);
      toast.success("Historique efface");
    } catch (err) {
      console.error("Error clearing conversations:", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setClearAllDialogOpen(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const groupedConversations = conversations.reduce((acc, conv) => {
    const dateKey = formatDate(conv.updated_at || conv.created_at);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(conv);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-30 safe-top">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <History className="w-5 h-5" />
              <h1 className="font-semibold text-lg">Historique</h1>
            </div>
          </div>
          {conversations.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setClearAllDialogOpen(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Tout effacer
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 safe-bottom">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold text-lg mb-2">Aucune conversation</h3>
            <p className="text-muted-foreground mb-4">
              Commence a discuter avec un expert IA
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              Voir les experts
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedConversations).map(([date, convs]) => (
              <div key={date}>
                <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {date}
                </h2>
                <div className="space-y-2">
                  {convs.map((conv) => (
                    <Card 
                      key={conv.conversation_id}
                      className="group cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ backgroundColor: AGENT_COLORS[conv.agent_type] || "#6B7280" }}
                          onClick={() => navigate(`/agent/${conv.agent_type}?conversation=${conv.conversation_id}`)}
                        >
                          {AGENT_NAMES[conv.agent_type]?.charAt(0) || "?"}
                        </div>
                        <div 
                          className="flex-1 min-w-0"
                          onClick={() => navigate(`/agent/${conv.agent_type}?conversation=${conv.conversation_id}`)}
                          data-testid={`conversation-item-${conv.conversation_id}`}
                        >
                          <p className="font-medium truncate">
                            {conv.title || AGENT_NAMES[conv.agent_type] || conv.agent_type}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {AGENT_NAMES[conv.agent_type] || conv.agent_type}
                            {" · "}
                            {conv.message_count || 0} message{(conv.message_count || 0) > 1 ? "s" : ""}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConversationToDelete(conv.conversation_id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight 
                          className="w-5 h-5 text-muted-foreground flex-shrink-0"
                          onClick={() => navigate(`/agent/${conv.agent_type}?conversation=${conv.conversation_id}`)}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete single conversation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette conversation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irreversible. La conversation et tous ses messages seront definitivement supprimes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConversation}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear all dialog */}
      <AlertDialog open={clearAllDialogOpen} onOpenChange={setClearAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Effacer tout l'historique ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irreversible. Toutes tes conversations ({conversations.length}) seront definitivement supprimees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAll}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Tout effacer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
